import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { WizardNavigationMode, WizardStep, WizardStepErrors } from "../types";

interface UseWizardStateArgs<TData> {
  steps: WizardStep<TData>[];
  initialData: TData;
  navigationMode?: WizardNavigationMode;
}

interface UseWizardStateResult<TData> {
  activeStep: number;
  data: TData;
  setData: (patch: Partial<TData> | ((prev: TData) => Partial<TData>)) => void;
  errors: WizardStepErrors;
  tryNext: () => void;
  goNext: () => void;
  goBack: () => void;
  goToStep: (index: number) => void;
  navigateToStep: (index: number) => void;
  validateAll: () => boolean;
  isStepCompleted: (index: number) => boolean;
  isStepReachable: (index: number) => boolean;
  reset: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  isDirty: boolean;
}

export const useWizardState = <TData>({
  steps,
  initialData,
  navigationMode = "sequential",
}: UseWizardStateArgs<TData>): UseWizardStateResult<TData> => {
  const [activeStep, setActiveStep] = useState(0);
  const [data, setDataState] = useState<TData>(initialData);
  const [errors, setErrors] = useState<WizardStepErrors>({});
  const [isDirty, setIsDirty] = useState(false);
  const [completedKeys, setCompletedKeys] = useState<string[]>([]);

  const isFree = navigationMode === "free";

  const isSkipped = useCallback(
    (index: number) => !!steps[index]?.skip?.(data),
    [steps, data],
  );

  const nextIndex = useMemo(() => {
    for (let i = activeStep + 1; i < steps.length; i += 1) {
      if (!isSkipped(i)) return i;
    }
    return -1;
  }, [activeStep, steps.length, isSkipped]);

  const prevIndex = useMemo(() => {
    for (let i = activeStep - 1; i >= 0; i -= 1) {
      if (!isSkipped(i)) return i;
    }
    return 0;
  }, [activeStep, isSkipped]);

  const stepsSignature = steps
    .map((s, index) => `${s.key}:${isSkipped(index) ? 0 : 1}`)
    .join("|");
  const prevSignatureRef = useRef(stepsSignature);
  const activeStepRef = useRef(activeStep);
  activeStepRef.current = activeStep;

  useEffect(() => {
    if (prevSignatureRef.current === stepsSignature) return;
    prevSignatureRef.current = stepsSignature;
    if (isFree) return;
    const keysUpToActive = steps
      .slice(0, activeStepRef.current + 1)
      .map((s) => s.key);
    setCompletedKeys((prev) => {
      const next = prev.filter((k) => keysUpToActive.includes(k));
      return next.length === prev.length ? prev : next;
    });
  }, [stepsSignature, steps, isFree]);

  const markCompleted = useCallback((key: string) => {
    setCompletedKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
  }, []);

  const isStepCompleted = useCallback(
    (index: number) => {
      const step = steps[index];
      if (!step || isSkipped(index)) return false;
      return isFree || completedKeys.includes(step.key);
    },
    [steps, isSkipped, isFree, completedKeys],
  );

  const isStepReachable = useCallback(
    (index: number) => {
      if (index === activeStep) return false;
      if (index < 0 || index >= steps.length || isSkipped(index)) return false;
      return isStepCompleted(index);
    },
    [activeStep, steps.length, isSkipped, isStepCompleted],
  );

  const setData = useCallback(
    (patch: Partial<TData> | ((prev: TData) => Partial<TData>)) => {
      setIsDirty(true);
      setDataState((prev) => {
        const resolved = typeof patch === "function" ? patch(prev) : patch;

        const changedKeys = Object.keys(resolved);
        if (changedKeys.length > 0) {
          setErrors((prevErrors) => {
            let mutated = false;
            const next: WizardStepErrors = { ...prevErrors };
            for (const k of changedKeys) {
              if (k in next) {
                delete next[k];
                mutated = true;
              }

              for (const errKey of Object.keys(next)) {
                if (errKey.startsWith(`${k}.`)) {
                  delete next[errKey];
                  mutated = true;
                }
              }
            }
            return mutated ? next : prevErrors;
          });
        }
        return { ...prev, ...resolved };
      });
    },
    [],
  );

  const leaveCurrentStep = useCallback((): boolean => {
    const current = steps[activeStep];
    if (!current) return false;
    if (current.validate) {
      const result = current.validate(data);
      if (!result.valid) {
        setErrors(result.errors);
        return false;
      }
    }
    if (current.onLeave) {
      const cleanup = current.onLeave(data);
      if (cleanup && Object.keys(cleanup).length > 0) {
        setDataState((prev) => ({ ...prev, ...cleanup }));
      }
    }
    setErrors({});
    return true;
  }, [steps, activeStep, data]);

  const tryNext = useCallback(() => {
    if (!leaveCurrentStep()) return;
    const current = steps[activeStep];
    if (current) markCompleted(current.key);
    if (nextIndex >= 0) setActiveStep(nextIndex);
  }, [leaveCurrentStep, steps, activeStep, markCompleted, nextIndex]);

  const goNext = useCallback(() => {
    if (nextIndex < 0) return;
    setErrors({});
    setActiveStep(nextIndex);
  }, [nextIndex]);

  const goBack = useCallback(() => {
    setErrors({});
    setActiveStep(prevIndex);
  }, [prevIndex]);

  const goToStep = useCallback(
    (index: number) => {
      if (index < 0 || index >= steps.length || isSkipped(index)) return;
      setErrors({});
      setActiveStep(index);
    },
    [steps.length, isSkipped],
  );

  const navigateToStep = useCallback(
    (index: number) => {
      if (!isStepReachable(index)) return;
      if (index > activeStep && !leaveCurrentStep()) return;
      setErrors({});
      setActiveStep(index);
    },
    [isStepReachable, activeStep, leaveCurrentStep],
  );

  const validateAll = useCallback((): boolean => {
    for (let i = 0; i < steps.length; i += 1) {
      const step = steps[i];
      if (isSkipped(i) || !step.validate) continue;
      const result = step.validate(data);
      if (!result.valid) {
        setActiveStep(i);
        setErrors(result.errors);
        return false;
      }
    }
    return true;
  }, [steps, isSkipped, data]);

  const reset = useCallback(() => {
    setActiveStep(0);
    setDataState(initialData);
    setErrors({});
    setIsDirty(false);
    setCompletedKeys([]);
  }, [initialData]);

  return {
    activeStep,
    data,
    setData,
    errors,
    tryNext,
    goNext,
    goBack,
    goToStep,
    navigateToStep,
    validateAll,
    isStepCompleted,
    isStepReachable,
    reset,
    isFirstStep: activeStep === 0,
    isLastStep: nextIndex < 0,
    isDirty,
  };
};
